/**
 * §8.7 / `content/bosses.json` — the four scheduled encounters, their HP-threshold phase
 * ladders, scripted attack patterns, enrage timers and arena transitions (§10.6).
 *
 * Every pattern telegraphs before it can damage (G1, §8.14); every telegraph is floored at
 * 300 ms even under enrage. Randomness consumes `w.rng.spawn` only — boss scripts are part
 * of the seeded plan (§8.12).
 */

import type { World } from './world';
import { POOL_CAPACITY } from './world';
import type { Enemy, Projectile } from './entities';
import { BOSSES, difficultyById } from '../core/content';
import {
  HAZARD_TELEGRAPH_MIN_TICKS,
  applyPlayerDamage,
  hazardPush,
  releaseHazardsOwnedBy,
  spawnBossTide,
  spawnBossVents,
  spawnLastLight,
  spawnMortarPool,
} from './hazards';
import { spawnEnemy } from './enemies';
import { setVictory } from './damage';
import { wrapDeltaX, wrapDeltaY, wrapPos, wrapX, wrapY } from './wrap';

/** Preallocated affix argument buffers — R3's `spawnEnemy` reads `affixes.length`. */
const AFFIX_NONE: string[] = [];
const AFFIX_ONE: string[] = [''];
const AFFIX_TWO: string[] = ['', ''];

/**
 * R3 boundary — the single place R4 calls `spawnEnemy`. R3 always applies its own 1.5 s
 * spawn telegraph (`director.telegraph_min_s`); `telegraphTicks` extends it when the
 * director needs a longer portal (stream entry stagger, §8.5).
 */
export function spawnEnemyExternal(
  w: World,
  defId: string,
  x: number,
  y: number,
  elite: boolean,
  affixA: string,
  affixB: string,
  telegraphTicks: number,
): Enemy | null {
  let affixes = AFFIX_NONE;
  if (elite && affixB !== '') {
    AFFIX_TWO[0] = affixA;
    AFFIX_TWO[1] = affixB;
    affixes = AFFIX_TWO;
  } else if (elite && affixA !== '') {
    AFFIX_ONE[0] = affixA;
    affixes = AFFIX_ONE;
  }
  const e = spawnEnemy(w, defId, x, y, elite, affixes);
  if (e === null) return null;
  const want = w.tick + telegraphTicks;
  if (want > e.telegraph_until) {
    const delta = want - e.telegraph_until;
    e.telegraph_until = want;
    e.attack_ready_tick += delta;
  }
  return e;
}

// --- constants ---

/** §8.7 intro framing: 3 s dedicated camera/title, all trash spawns paused. */
export const BOSS_INTRO_TICKS = 180;
/** Every boss telegraph floor, including enrage (`bosses.json` enrage.telegraph_floor_ms). */
export const TELEGRAPH_FLOOR_TICKS = 18;
/** §8.11 — summoned adds use ≥ 1.5 s portals outside 12 u. */
export const SUMMON_PORTAL_TICKS = HAZARD_TELEGRAPH_MIN_TICKS;
/** §10.6 — sovereign phase 3 contracts the arena 10 u per side over 3 s. */
export const CONTRACTION_TICKS = 180;
export const CONTRACTION_AMOUNT = 10;
/** Rate at which entities left outside the shrinking bounds are nudged in (§10.6). */
export const CONTRACTION_NUDGE = 5;

const BOSS_PROJECTILE_RADIUS = 0.4;
const BEAM_HALF_WIDTH = 1.5;
const RING_HALF_THICKNESS = 1.2;
const SPAWN_SAFE_R = 12;
const BOSS_SPAWN_DISTANCE = 26;

// --- engagement constants (AMD-24 follow-up) ---

/** §3.2 player top speed. A boss slower than this loses every straight footrace. */
const PLAYER_TOP_SPEED = 8;
/**
 * Leash. Nothing on a torus ever forces a fight: there is no wall to back into and no
 * centre to converge on, so a 3 u/s boss that only pursues is a boss you never see again.
 * Past its phase band plus `LEASH_SLACK` the boss ramps toward `LEASH_TOP_SPEED` over
 * `LEASH_SPAN` u — above the player's 8 u/s, so distance always closes eventually. Inside
 * the band the ramp is inert, so the fight still reads as "big slow thing" while you fight it.
 */
const LEASH_SLACK = 6;
const LEASH_RAMP = 5;
const LEASH_SPAN = 30;
/**
 * Floor, not a target. A ramp that merely *approaches* the player's speed has a stable
 * equilibrium: the first version of this settled at exactly 8.0 u/s and a fleeing player
 * held a 49 u gap forever. The floor has to sit above 8 or the leash is decorative.
 */
const LEASH_FLOOR_SPEED = 8.7;
const LEASH_TOP_SPEED = 10.5;
/** Cap on how far ahead of the player a gait may aim, in seconds. */
const MAX_LEAD_S = 1.6;
/** Crown Medusa: extra closing speed per unit of the player's recede rate. */
const STALK_GAIN = 1.35;
/** Abyssal Warden: how far past the intercept point it aims, so it arrives in front. */
const CUT_AHEAD = 7;
/** How far past the player a Warden Roll carries before the lane-end impact. */
const ROLL_OVERSHOOT = 10;

// --- attack kinds (numeric so the hot path never compares strings) ---

const enum Kind {
  Volley = 0,
  Lunge = 1,
  Summon = 2,
  Ring = 3,
  Roll = 4,
  MeleeArc = 5,
  Mortar = 6,
  DartRing = 7,
  ArmSweep = 8,
  TidePull = 9,
  Beam = 10,
  CurrentTide = 11,
  Expose = 12,
  Barrage = 13,
  LastLight = 14,
}

interface AttackParams {
  kind: Kind;
  /** Projectile / dart / summon count. */
  count: number;
  /** Total fan spread in degrees; 360 means a full radial. */
  spread: number;
  /** Projectile speed (u/s), ring speed, roll speed or pull speed depending on kind. */
  speed: number;
  /** Number of safe gaps in a radial preview. */
  gaps: number;
  /** Width of each safe gap in degrees. */
  gapDeg: number;
  /** Pattern duration in ticks (lunge, beam sweep, arm sweep, pull, exposure). */
  activeTicks: number;
  /** Melee arc range / ring radius / safe-disc radius. */
  range: number;
  /** Chained repetitions (chained lunges, barrage waves). */
  stages: number;
  /** Ticks between chained stages. */
  stageGap: number;
  primary: string;
  secondary: string;
  maxAlive: number;
}

interface CompiledAttack extends AttackParams {
  id: string;
  telegraphTicks: number;
  cooldownTicks: number;
  damage: number;
}

/**
 * Movement modes. Every one of them resolves against the PLAYER, never against an arena
 * coordinate — see `moveBoss`. Mode 1 used to read `atan2(e.pos.y, e.pos.x)` and mode 2
 * used to target literal `(0, 0)`; on the AMD-24 torus those are arbitrary points with no
 * relationship to where the player is, which is the whole reported bug.
 */
const enum Move {
  /** Hold `near..far` from the player's lead point, sweeping tangentially. */
  Band = 0,
  /** Circle-strafe the player at `far`. */
  OrbitPlayer = 1,
  /** Stand a post pinned to the player: hold it, uproot and re-plant when they leave. */
  Anchor = 2,
}

/** Per-boss steering personality. One sentence each; see `GAIT_TABLE`. */
const enum Gait {
  /** Lantern Tyrant — runs at where you are going, gets in front, commits. */
  Charger = 0,
  /** Abyssal Warden — never chases; cuts across your line and rolls through it. */
  Cutter = 1,
  /** Crown Medusa — never backs off, and speeds up the moment you turn your back. */
  Stalker = 2,
  /** Hadal Sovereign — stands off and sweeps around you, then plants and denies the space. */
  Splitter = 3,
}

interface PhaseMove {
  mode: Move;
  near: number;
  far: number;
  speed: number;
  /** Tangential sweep rate in deg/s around the player. 0 means pure radial. */
  orbit: number;
  /** §8.7 pattern-cadence multiplier applied to every cooldown in the phase. */
  cadence: number;
}

interface CompiledPhase {
  index: number;
  hpThreshold: number;
  attacks: CompiledAttack[];
  move: PhaseMove;
}

interface CompiledBoss {
  id: string;
  kind: string;
  hp: number;
  radius: number;
  gait: Gait;
  phases: CompiledPhase[];
  enrageTicks: number;
  enrageMove: number;
  enrageCooldown: number;
  enrageTelegraph: number;
  encounterMinute: number;
}

const DEFAULT_PARAMS: AttackParams = {
  kind: Kind.Volley,
  count: 1,
  spread: 0,
  speed: 10,
  gaps: 0,
  gapDeg: 0,
  activeTicks: 0,
  range: 0,
  stages: 1,
  stageGap: 0,
  primary: '',
  secondary: '',
  maxAlive: 0,
};

function params(over: Partial<AttackParams>): AttackParams {
  return { ...DEFAULT_PARAMS, ...over };
}

/** Attack id → mechanical parameters. Numbers come straight from `bosses.json` details. */
const ATTACK_TABLE: Record<string, AttackParams> = {
  'barb-volley': params({ kind: Kind.Volley, count: 3, spread: 15, speed: 10 }),
  'barb-volley-5': params({ kind: Kind.Volley, count: 5, spread: 30, speed: 10 }),
  'tyrant-lunge': params({ kind: Kind.Lunge, speed: 14, activeTicks: 60, stages: 1, stageGap: 36, range: 0.5 }),
  'call-of-the-deep': params({ kind: Kind.Summon, count: 6, primary: 'enemy.glimmerfry', maxAlive: 24 }),
  'dark-pulse': params({ kind: Kind.Ring, speed: 8, gaps: 1, gapDeg: 25, activeTicks: 300 }),
  'warden-roll': params({ kind: Kind.Roll, speed: 20, activeTicks: 300, range: 0.5 }),
  'antenna-whip': params({ kind: Kind.MeleeArc, spread: 90, range: 5 }),
  'mortar-barbs': params({ kind: Kind.Mortar, count: 3 }),
  'dart-ring': params({ kind: Kind.DartRing, count: 16, speed: 8, gaps: 2, gapDeg: 22.5 }),
  'arm-sweep': params({ kind: Kind.ArmSweep, count: 4, spread: 45, speed: 18, range: 9, activeTicks: 300 }),
  'sting-burst': params({ kind: Kind.Volley, count: 8, spread: 360, speed: 9 }),
  'brood-call': params({ kind: Kind.Summon, count: 3, primary: 'enemy.glimmerfry', maxAlive: 12 }),
  'bell-slam': params({ kind: Kind.Ring, speed: 10, gaps: 1, gapDeg: 30, activeTicks: 300 }),
  'tide-pull': params({ kind: Kind.TidePull, speed: 2, activeTicks: 180 }),
  'sovereign-beam': params({ kind: Kind.Beam, spread: 60, speed: 20, activeTicks: 180 }),
  'bone-field': params({ kind: Kind.Summon, count: 4, primary: 'enemy.driftfin', secondary: 'enemy.basalt-crab', maxAlive: 12 }),
  'abyssal-tide': params({ kind: Kind.CurrentTide, count: 3, activeTicks: 240 }),
  'curtain-call': params({ kind: Kind.Summon, count: 1, primary: 'enemy.colonial-curtain', maxAlive: 2 }),
  'heart-of-the-abyss': params({ kind: Kind.Expose, activeTicks: 360 }),
  'lantern-barrage': params({ kind: Kind.Barrage, count: 6, spread: 40, speed: 10, stages: 2, stageGap: 30 }),
  'dart-ring-dense': params({ kind: Kind.DartRing, count: 24, speed: 9, gaps: 3, gapDeg: 15 }),
  'last-light': params({ kind: Kind.LastLight, range: 6, activeTicks: 0 }),
};

/** Silhouette radii (`bosses.json` silhouette prose; sovereign occupies ~15 u diameter). */
const BOSS_RADIUS: Record<string, number> = {
  'boss.lantern-tyrant': 2.5,
  'boss.abyssal-warden': 3.0,
  'boss.crown-medusa': 4.0,
  'boss.hadal-sovereign': 7.5,
};

/** One gait per boss, so no two encounters steer alike (§8.7 "each boss teaches a lesson"). */
const GAIT_TABLE: Record<string, Gait> = {
  'boss.lantern-tyrant': Gait.Charger,
  'boss.abyssal-warden': Gait.Cutter,
  'boss.crown-medusa': Gait.Stalker,
  'boss.hadal-sovereign': Gait.Splitter,
};

/**
 * Per-phase movement, transcribed from each phase's `movement` line in `bosses.json`.
 *
 * `far` is also the leash radius: past `far + LEASH_SLACK` the boss stops being polite
 * about its band and closes. Phases change the SHAPE of the movement, not only its speed —
 * the warden stops crawling and starts circling you, the medusa stops drifting and starts
 * planting, the sovereign stops standing off and starts squatting on your ground.
 */
const MOVE_TABLE: Record<string, PhaseMove[]> = {
  'boss.lantern-tyrant': [
    // Stalks at a walk, always angled toward where you are heading.
    { mode: Move.Band, near: 8, far: 13, speed: 4.0, orbit: 12, cadence: 1 },
    // Backs off to summon, circling harder while the adds arrive.
    { mode: Move.Band, near: 10, far: 16, speed: 4.0, orbit: 28, cadence: 1 },
    // Drops the circling entirely: straight in, every time.
    { mode: Move.Band, near: 5, far: 9, speed: 4.0, orbit: 0, cadence: 1 / 1.3 },
  ],
  'boss.abyssal-warden': [
    { mode: Move.Band, near: 6, far: 12, speed: 3.5, orbit: 22, cadence: 1 },
    { mode: Move.Band, near: 8, far: 13, speed: 3.0, orbit: 34, cadence: 1 },
    // "curls and orbits" — around YOU, at 6 u/s, which is the phase's whole new problem.
    { mode: Move.OrbitPlayer, near: 0, far: 11, speed: 6.0, orbit: 46, cadence: 1 },
  ],
  'boss.crown-medusa': [
    // near === far: the stalker rule never lets `want` grow, so it only ever closes.
    { mode: Move.Band, near: 9, far: 9, speed: 2.5, orbit: 0, cadence: 1 },
    { mode: Move.Band, near: 8, far: 8, speed: 3.5, orbit: 8, cadence: 1 },
    // "stations between slams" — the station is a post planted next to you, re-planted
    // on every slam, so standing still is never the answer twice.
    { mode: Move.Anchor, near: 0, far: 5, speed: 3.5, orbit: 0, cadence: 1 },
  ],
  'boss.hadal-sovereign': [
    { mode: Move.Band, near: 12, far: 17, speed: 3.0, orbit: 20, cadence: 1 },
    { mode: Move.Band, near: 12, far: 17, speed: 3.5, orbit: 26, cadence: 1 },
    // Plants a standoff post at 16 u and holds the bearing: the beam then cuts a fixed
    // line through your half of the arena until you force it to re-plant.
    { mode: Move.Anchor, near: 0, far: 14, speed: 3.0, orbit: 0, cadence: 1 },
    // Phase 4 uproots the post and walks it around you while the arena dims (§11 darkness
    // pulses): the one phase where standing still is never safe for long.
    { mode: Move.Anchor, near: 0, far: 14, speed: 3.0, orbit: 16, cadence: 1 / 1.25 },
  ],
};

interface RawPhase {
  index: number;
  hp_threshold_pct: number;
  attacks: { id: string; name: string; telegraph_ms: number; cooldown_s: number; damage: number }[];
}

interface RawEnrage {
  trigger_time_s: number;
  effects: { move_speed_mult?: number; cooldown_mult?: number; telegraph_mult?: number; mortar_count?: number; beam_sweep_deg_s?: number; rotation_mult?: number };
}

function compile(): Record<string, CompiledBoss> {
  const out: Record<string, CompiledBoss> = {};
  for (const raw of BOSSES) {
    const id = raw.id;
    const rawPhases = raw.phases as unknown as RawPhase[];
    const enrage = raw.enrage as unknown as RawEnrage;
    const moves = MOVE_TABLE[id];
    const phases: CompiledPhase[] = [];
    for (let i = 0; i < rawPhases.length; i++) {
      const rp = rawPhases[i];
      const attacks: CompiledAttack[] = [];
      for (const ra of rp.attacks) {
        const base = ATTACK_TABLE[ra.id];
        if (base === undefined) continue;
        const p: AttackParams = { ...base };
        // "(chained x2)" in the display name is the only place the chain count lives.
        if (ra.name.indexOf('x2') >= 0) p.stages = 2;
        // Crown Medusa phase 2 speeds arm rotation to 24 deg/s.
        if (base.kind === Kind.ArmSweep && rp.index >= 2) p.speed = 24;
        // Sovereign phase 4 beam sweeps at 25 deg/s.
        if (base.kind === Kind.Beam && rp.index >= 4) p.speed = 25;
        attacks.push({
          ...p,
          id: ra.id,
          telegraphTicks: Math.max(TELEGRAPH_FLOOR_TICKS, Math.round((ra.telegraph_ms / 1000) * 60)),
          cooldownTicks: Math.round(ra.cooldown_s * 60),
          damage: ra.damage,
        });
      }
      phases.push({
        index: rp.index,
        hpThreshold: rp.hp_threshold_pct / 100,
        attacks,
        move: moves !== undefined && moves[i] !== undefined ? moves[i] : { mode: Move.Band, near: 8, far: 14, speed: 3, orbit: 15, cadence: 1 },
      });
    }
    out[id] = {
      id,
      kind: raw.kind as string,
      hp: raw.hp as number,
      radius: BOSS_RADIUS[id] !== undefined ? BOSS_RADIUS[id] : 3,
      gait: GAIT_TABLE[id] !== undefined ? GAIT_TABLE[id] : Gait.Charger,
      phases,
      enrageTicks: Math.round(enrage.trigger_time_s * 60),
      enrageMove: enrage.effects.move_speed_mult !== undefined ? enrage.effects.move_speed_mult : 1,
      enrageCooldown: enrage.effects.cooldown_mult !== undefined ? enrage.effects.cooldown_mult : 1,
      enrageTelegraph: enrage.effects.telegraph_mult !== undefined ? enrage.effects.telegraph_mult : 1,
      encounterMinute: raw.encounter_time_min as number,
    };
  }
  return out;
}

export const BOSS_TABLE: Readonly<Record<string, CompiledBoss>> = compile();

/** Encounter order, matching §8.7's schedule table. */
export const BOSS_SCHEDULE: readonly string[] = ['boss.lantern-tyrant', 'boss.abyssal-warden', 'boss.crown-medusa', 'boss.hadal-sovereign'];

// --- runtime state ---

/** Serializable boss-system state (A.6). */
export interface BossState {
  boss_id: string;
  entity_id: number;
  pool_index: number;
  spawn_tick: number;
  phase_index: number;
  enraged: number;
  attack_index: number;
  attack_start: number;
  attack_arm: number;
  active_until: number;
  stage: number;
  stage_next: number;
  next_ready: number[];
  aim_angle: number;
  aim_x: number;
  aim_y: number;
  /** `Move.Anchor` post, canonical (AMD-24). Derived from the player, never from the arena. */
  anchor_x: number;
  anchor_y: number;
  /** 0 = no post planted yet. */
  anchor_set: number;
  /** Which way this boss sweeps around the player. Deterministic, no RNG draw. */
  orbit_sign: number;
  /** Previous wrapped boss→player distance, for the stalker's recede rate. */
  prev_dist: number;
  gap_angle: number;
  ring_radius: number;
  hit_flag: number;
  exposed_until: number;
  recovery_until: number;
  contract_start: number;
  vents_done: number;
  adds_spawned: number;
  defeated_tick: number;
  last_defeated_id: string;
  victory_pending: number;
}

interface BossRuntime extends BossState {
  projOwned: Uint8Array;
  scratch: { x: number; y: number };
  /** Steering scratch, kept separate from `scratch` (which `applyCurrentToBoss` reuses). */
  lead: { x: number; y: number };
}

const RUNTIME = new WeakMap<World, BossRuntime>();

function runtime(w: World): BossRuntime {
  let st = RUNTIME.get(w);
  if (st === undefined) {
    st = {
      boss_id: '',
      entity_id: 0,
      pool_index: -1,
      spawn_tick: 0,
      phase_index: 0,
      enraged: 0,
      attack_index: -1,
      attack_start: 0,
      attack_arm: 0,
      active_until: 0,
      stage: 0,
      stage_next: 0,
      next_ready: new Array<number>(8).fill(0),
      aim_angle: 0,
      aim_x: 0,
      aim_y: 0,
      anchor_x: 0,
      anchor_y: 0,
      anchor_set: 0,
      orbit_sign: 1,
      prev_dist: -1,
      gap_angle: 0,
      ring_radius: 0,
      hit_flag: 0,
      exposed_until: 0,
      recovery_until: 0,
      contract_start: -1,
      vents_done: 0,
      adds_spawned: 0,
      defeated_tick: -1,
      last_defeated_id: '',
      victory_pending: 0,
      projOwned: new Uint8Array(POOL_CAPACITY.projectiles),
      scratch: { x: 0, y: 0 },
      lead: { x: 0, y: 0 },
    };
    RUNTIME.set(w, st);
  }
  return st;
}

let selfStepProjectiles = true;

/**
 * Boss projectiles live in the shared projectile pool with `faction: 'enemy'`. Until R2's
 * `stepProjectiles` integrates enemy-faction projectiles, R4 steps its own. Flip this off
 * once R2 owns them.
 */
export function setBossProjectileSelfStep(on: boolean): void {
  selfStepProjectiles = on;
}

export { setPlayerDamageSink, setStatusSink } from './hazards';

// --- queries ---

export function activeBossId(w: World): string {
  const st = runtime(w);
  return st.boss_id;
}

export function activeBossEntity(w: World): Enemy | null {
  const st = runtime(w);
  if (st.pool_index < 0) return null;
  const e = w.enemies.slots[st.pool_index];
  return e.alive && e.id === st.entity_id ? e : null;
}

export function bossPhaseIndex(w: World): number {
  return runtime(w).phase_index;
}

/** Tick at which the last boss died, or -1. The director reads this for §8.7 recovery. */
export function lastBossDefeatTick(w: World): number {
  return runtime(w).defeated_tick;
}

export function lastBossDefeatedId(w: World): string {
  return runtime(w).last_defeated_id;
}

/**
 * §8.7 armour / vulnerability resolver. `damage.ts` multiplies incoming damage by this.
 * `srcX/srcY` is the direction the hit arrives FROM, in world space.
 */
export function bossDamageMultiplier(w: World, e: Enemy, srcX: number, srcY: number): number {
  const st = runtime(w);
  if (!e.is_boss || e.id !== st.entity_id) return 1;
  const tick = w.tick;
  if (tick < st.exposed_until) return 1.5;
  if (tick < st.recovery_until) return 1.25;
  if (st.boss_id === 'boss.abyssal-warden') {
    const dx = wrapDeltaX(srcX - e.pos.x);
    const dy = wrapDeltaY(srcY - e.pos.y);
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 1e-6) {
      const dot = (Math.cos(e.steering.heading) * dx + Math.sin(e.steering.heading) * dy) / len;
      // 120 deg frontal arc -> half-angle 60 deg -> cos 0.5.
      if (dot >= 0.5) return 0.5;
    }
  }
  return 1;
}

// --- spawn ---

/**
 * §8.7 — scheduled boss spawn. Bosses are not enemy-table archetypes, so they are built
 * directly on the shared enemy pool rather than through R3's `spawnEnemy`.
 * Sets `w.cinematicUntil` for the 3 s intro framing and emits `boss_spawn`.
 */
export function spawnBoss(w: World, bossId: string): Enemy | null {
  const def = BOSS_TABLE[bossId];
  if (def === undefined) return null;
  const e = w.enemies.acquire();
  if (e === null) {
    w.bus.emit('pool_exhausted', { pool: 'enemies', denied_kind: bossId });
    return null;
  }
  const st = runtime(w);
  const angle = w.rng.spawn.next() * Math.PI * 2;
  // No wall to be pushed off: the ring around the player always lands somewhere legal.
  const x = wrapX(w.player.pos.x + Math.cos(angle) * BOSS_SPAWN_DISTANCE);
  const y = wrapY(w.player.pos.y + Math.sin(angle) * BOSS_SPAWN_DISTANCE);

  const hp = def.hp * bossHpMultiplier(w);
  e.id = w.allocId();
  e.def_id = bossId;
  e.pos.x = x;
  e.pos.y = y;
  e.vel.x = 0;
  e.vel.y = 0;
  e.hp = hp;
  e.max_hp = hp;
  e.hp_frac = 1;
  e.hitbox_radius = def.radius;
  e.faction = 'enemy';
  // §13.1.7: "framing doubles as the spawn telegraph (§8.11)". A telegraph means
  // not-yet-material, so the boss must not be hittable during its own intro. It was
  // `true` from tick 0, so auto-weapons chipped a boss for the whole 3 s while §13.1.7
  // holds the player's input locked — free damage with no agency, and the §13.1.4 boss
  // bar visibly draining through its own reveal.
  //
  // `telegraph_until` is set to the intro end a few lines below, and `stepLifecycle`
  // (§8.5) already ramps `spawn_anim` and flips `targetable` once that elapses — the
  // same path trash takes. Setting this false is the whole fix.
  e.targetable = false;
  e.elite = false;
  e.affix_count = 0;
  e.threat_score = 0;
  e.status_count = 0;
  e.spawn_tick = w.tick;
  e.age_ticks = 0;
  e.is_boss = true;
  e.scale = def.radius;
  e.boss_phase_index = 1;
  e.boss_enrage_tick = w.tick + def.enrageTicks;
  e.telegraph_until = w.tick + BOSS_INTRO_TICKS;
  e.attack_state = 0;
  e.attack_ready_tick = 0;
  e.stagger_until = 0;
  e.contact_cd_until = 0;
  e.knock.x = 0;
  e.knock.y = 0;
  e.spawn_anim = 0;
  e.death_anim = 0;
  e.steering.heading = Math.atan2(wrapDeltaY(w.player.pos.y - y), wrapDeltaX(w.player.pos.x - x));
  e.steering.goal.x = w.player.pos.x;
  e.steering.goal.y = w.player.pos.y;
  e.steering.formation_slot = 0;
  e.steering.wander = 0;

  st.boss_id = bossId;
  st.entity_id = e.id;
  st.pool_index = e.pool_index;
  st.spawn_tick = w.tick;
  st.phase_index = 0;
  st.enraged = 0;
  st.attack_index = -1;
  st.attack_start = 0;
  st.attack_arm = 0;
  st.active_until = 0;
  st.stage = 0;
  st.stage_next = 0;
  st.aim_angle = e.steering.heading;
  st.anchor_x = 0;
  st.anchor_y = 0;
  st.anchor_set = 0;
  // Deterministic sweep direction — no RNG draw, so the `spawn` stream stays where it was.
  st.orbit_sign = (e.id & 1) === 0 ? 1 : -1;
  st.prev_dist = -1;
  st.ring_radius = 0;
  st.hit_flag = 0;
  st.exposed_until = 0;
  st.recovery_until = 0;
  st.contract_start = -1;
  st.vents_done = 0;
  st.adds_spawned = 0;
  st.victory_pending = 0;
  // Intro framing doubles as the telegraph (§8.11): nothing fires during the 3 s.
  const readyAt = w.tick + BOSS_INTRO_TICKS;
  for (let i = 0; i < st.next_ready.length; i++) st.next_ready[i] = readyAt;

  w.cinematicUntil = w.tick + BOSS_INTRO_TICKS;
  w.bus.emit('boss_spawn', { boss_id: bossId, entity_id: e.id });
  w.bus.emit('spawn', { entity_id: e.id, kind: 'enemy', def_id: bossId, pos: e.pos, elite: false, affixes: e.affixes });
  return e;
}

function bossHpMultiplier(w: World): number {
  const tier = difficultyById.get(w.difficultyId);
  if (tier === undefined) return 1;
  const mults = (tier as unknown as { stat_multipliers?: Record<string, number> }).stat_multipliers;
  if (mults === undefined || mults.boss_hp === undefined) return 1;
  return mults.boss_hp;
}

// --- step ---

/** Step 5 — boss phase logic and scripted patterns. */
export function stepBoss(w: World): void {
  if (w.outcome !== 'running') return;
  const st = runtime(w);
  if (selfStepProjectiles) stepBossProjectiles(w, st);
  if (st.boss_id === '') return;

  const e = activeBossEntity(w);
  if (e === null || e.hp <= 0) {
    onBossDefeated(w, st);
    return;
  }

  const def = BOSS_TABLE[st.boss_id];
  const tick = w.tick;
  e.age_ticks++;

  // §13.1.7 / §8.11 — materialisation is driven by the intro, NOT by `telegraph_until`.
  // That field is dual-purpose: the intro sets it, and every attack wind-up pushes it
  // forward again (see `armTelegraph`). `stepLifecycle`'s generic ramp gates on it, so a
  // boss got two ticks of birth and then stalled at `spawn_anim` 0.167 forever — the same
  // overloaded-flag bug that made every spawning enemy draw an aim line due east.
  if (tick < w.cinematicUntil) {
    const prog = (tick - e.spawn_tick) / BOSS_INTRO_TICKS;
    e.spawn_anim = prog < 0 ? 0 : prog > 1 ? 1 : prog;
    e.targetable = false;
    return;
  }
  if (e.spawn_anim < 1) {
    e.spawn_anim = 1;
    e.targetable = true;
    w.bus.emit('materialize', { entity_id: e.id });
  }

  if (st.enraged === 0 && tick >= st.spawn_tick + def.enrageTicks) {
    st.enraged = 1;
    e.boss_enrage_tick = tick;
  }

  advancePhase(w, st, def, e);
  moveBoss(w, st, def, e);
  runContraction(w, st, e);

  if (st.attack_index >= 0) {
    const atk = def.phases[st.phase_index].attacks[st.attack_index];
    if (tick === st.attack_arm) fireAttack(w, st, def, e, atk);
    if (tick >= st.attack_arm && tick < st.active_until) updateActive(w, st, def, e, atk);
    if (tick >= st.active_until && tick >= st.attack_arm) {
      st.attack_index = -1;
      e.attack_state = 0;
      e.telegraph_until = 0;
    } else if (tick < st.attack_arm) {
      e.attack_state = 1;
    } else {
      e.attack_state = 2;
    }
  } else {
    selectAttack(w, st, def, e);
  }
}

function advancePhase(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy): void {
  const frac = e.max_hp > 0 ? e.hp / e.max_hp : 0;
  e.hp_frac = frac;
  let target = st.phase_index;
  for (let i = def.phases.length - 1; i >= 0; i--) {
    if (frac <= def.phases[i].hpThreshold) {
      target = i;
      break;
    }
  }
  if (target <= st.phase_index) return;
  st.phase_index = target;
  e.boss_phase_index = def.phases[target].index;
  st.attack_index = -1;
  st.active_until = 0;
  // A new phase means a new shape of movement; any post from the old one is stale.
  st.anchor_set = 0;
  st.prev_dist = -1;
  e.attack_state = 0;
  e.telegraph_until = 0;
  const ready = w.tick + 30;
  for (let i = 0; i < st.next_ready.length; i++) st.next_ready[i] = ready;
  w.bus.emit('boss_phase', { boss_id: def.id, index: def.phases[target].index });
  onPhaseEnter(w, st, def, e);
}

/** §10.6 arena transitions keyed to phase entry. */
function onPhaseEnter(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy): void {
  const phaseNo = def.phases[st.phase_index].index;
  if (def.id === 'boss.abyssal-warden' && phaseNo === 2 && st.vents_done === 0) {
    st.vents_done = 1;
    spawnBossVents(w, e.id, st.aim_angle);
  }
  if (def.id === 'boss.hadal-sovereign' && phaseNo === 3 && st.contract_start < 0) {
    st.contract_start = w.tick;
  }
}

function runContraction(w: World, st: BossRuntime, e: Enemy): void {
  if (st.contract_start < 0) return;
  const t = w.tick - st.contract_start;
  if (t > CONTRACTION_TICKS + 60) return;
  const k = t >= CONTRACTION_TICKS ? 1 : t / CONTRACTION_TICKS;
  const half = 100 - CONTRACTION_AMOUNT * k;
  w.arenaHalfW = half;
  w.arenaHalfH = half;
  nudgeInside(w, w.player.pos, w.player.hitbox_radius);
  const list = w.enemies.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const en = w.enemies.slots[list[i]];
    nudgeInside(w, en.pos, en.hitbox_radius);
  }
  nudgeInside(w, e.pos, e.hitbox_radius);
}

function nudgeInside(w: World, pos: { x: number; y: number }, radius: number): void {
  const hw = w.arenaHalfW - radius;
  const hh = w.arenaHalfH - radius;
  const step = CONTRACTION_NUDGE / 60;
  if (pos.x > hw) pos.x = Math.max(hw, pos.x - Math.max(step, pos.x - hw));
  else if (pos.x < -hw) pos.x = Math.min(-hw, pos.x + Math.max(step, -hw - pos.x));
  if (pos.y > hh) pos.y = Math.max(hh, pos.y - Math.max(step, pos.y - hh));
  else if (pos.y < -hh) pos.y = Math.min(-hh, pos.y + Math.max(step, -hh - pos.y));
}

/**
 * Ramp toward `LEASH_TOP_SPEED` once the player is outside the phase band. `base` is the
 * phase cruise; the return is never below it.
 */
function leashSpeed(base: number, dPlayer: number, band: number): number {
  const edge = band + LEASH_SLACK;
  if (dPlayer <= edge) return base;
  const over = dPlayer - edge;
  // Over the first 5 u outside the band, wind up from cruise to just above the player's
  // 8 u/s; past that, keep pulling ahead so a long run costs ground rather than buying it.
  const k = over < LEASH_RAMP ? over / LEASH_RAMP : 1;
  let s = base + (LEASH_FLOOR_SPEED - base) * k;
  if (over > LEASH_RAMP) {
    const k2 = (over - LEASH_RAMP) / LEASH_SPAN;
    s += (LEASH_TOP_SPEED - LEASH_FLOOR_SPEED) * (k2 > 1 ? 1 : k2);
  }
  return s > base ? s : base;
}

/**
 * The point a gait actually steers at, written into `st.scratch`. Always the player plus a
 * gait-specific lead — a wrapped, player-relative quantity, never an arena coordinate.
 */
function leadPoint(w: World, def: CompiledBoss, dPlayer: number, speed: number, out: { x: number; y: number }): void {
  const p = w.player;
  out.x = p.pos.x;
  out.y = p.pos.y;
  if (def.gait === Gait.Stalker) return; // a stalker walks at you, not at your future
  let t = dPlayer / (speed > 1 ? speed : 1);
  if (t > MAX_LEAD_S) t = MAX_LEAD_S;
  out.x += p.vel.x * t;
  out.y += p.vel.y * t;
  if (def.gait !== Gait.Cutter) return;
  // A cutter aims past the intercept, so it arrives in front of you rather than behind.
  const vx = p.vel.x;
  const vy = p.vel.y;
  const vm = Math.sqrt(vx * vx + vy * vy);
  if (vm < 1e-3) return;
  out.x += (vx / vm) * CUT_AHEAD;
  out.y += (vy / vm) * CUT_AHEAD;
}

/** Plant the `Move.Anchor` post at `far` from the player, on the boss's current bearing. */
function plantAnchor(w: World, st: BossRuntime, e: Enemy, radius: number): void {
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  let a = Math.atan2(wrapDeltaY(e.pos.y - py), wrapDeltaX(e.pos.x - px));
  if (!(a === a)) a = st.aim_angle;
  st.anchor_x = wrapX(px + Math.cos(a) * radius);
  st.anchor_y = wrapY(py + Math.sin(a) * radius);
  st.anchor_set = 1;
}

function moveBoss(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy): void {
  const phase = def.phases[st.phase_index];
  const mv = phase.move;
  const tick = w.tick;
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  const pdx = wrapDeltaX(px - e.pos.x);
  const pdy = wrapDeltaY(py - e.pos.y);
  const dPlayer = Math.sqrt(pdx * pdx + pdy * pdy);
  const recede = st.prev_dist < 0 ? 0 : (dPlayer - st.prev_dist) * 60;
  st.prev_dist = dPlayer;

  if (tick < e.stagger_until) return;
  // Lunge and roll drive the body themselves.
  if (st.attack_index >= 0 && tick >= st.attack_arm && tick < st.active_until) {
    const k = phase.attacks[st.attack_index].kind;
    if (k === Kind.Lunge || k === Kind.Roll) return;
  }

  let speed = mv.speed * (st.enraged === 1 ? def.enrageMove : 1);
  if (def.gait === Gait.Stalker) {
    // The one gait whose acceleration you feel inside the fight: turn your back and it
    // gains on you, walk into it and it slows to a drift.
    let k = recede / PLAYER_TOP_SPEED;
    if (k < 0) k = 0;
    else if (k > 1) k = 1;
    speed *= 1 + STALK_GAIN * k;
  }
  speed = leashSpeed(speed, dPlayer, mv.far);

  const ref = st.lead;
  leadPoint(w, def, dPlayer, speed, ref);
  const rx = ref.x;
  const ry = ref.y;

  let tx: number;
  let ty: number;
  if (mv.mode === Move.Anchor) {
    if (st.anchor_set === 0) plantAnchor(w, st, e, mv.far);
    // The post is pinned to the player: walk far enough away and it uproots and follows.
    const adx = wrapDeltaX(st.anchor_x - px);
    const ady = wrapDeltaY(st.anchor_y - py);
    if (adx * adx + ady * ady > (mv.far + LEASH_SLACK) * (mv.far + LEASH_SLACK)) {
      plantAnchor(w, st, e, mv.far);
    } else if (mv.orbit !== 0) {
      // A walking post: the boss holds its standoff but the standoff itself creeps around
      // the player. Keeps a "stationary" phase from being a phase you can stand still in.
      const a = Math.atan2(ady, adx) + ((mv.orbit * Math.PI) / 180 / 60) * st.orbit_sign;
      st.anchor_x = wrapX(px + Math.cos(a) * mv.far);
      st.anchor_y = wrapY(py + Math.sin(a) * mv.far);
    }
    tx = st.anchor_x;
    ty = st.anchor_y;
  } else {
    // Boss position expressed relative to the lead point — the only frame that means
    // anything on a torus. `want` is the radius it wants to hold; the tangential term
    // makes it sweep around you instead of sliding along one fixed ray.
    const bdx = wrapDeltaX(e.pos.x - rx);
    const bdy = wrapDeltaY(e.pos.y - ry);
    const d = Math.sqrt(bdx * bdx + bdy * bdy);
    let want: number;
    if (mv.mode === Move.OrbitPlayer) want = mv.far;
    else if (def.gait === Gait.Stalker) want = d < mv.far ? d : mv.far; // never retreats
    else want = d < mv.near ? mv.far : d > mv.far ? mv.near : d;
    const a0 = d > 1e-4 ? Math.atan2(bdy, bdx) : st.aim_angle;
    const sweep = ((mv.orbit * Math.PI) / 180 / 60) * st.orbit_sign;
    const a = a0 + sweep;
    tx = rx + Math.cos(a) * want;
    ty = ry + Math.sin(a) * want;
  }

  const dx = wrapDeltaX(tx - e.pos.x);
  const dy = wrapDeltaY(ty - e.pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d > 1e-4 && speed > 0) {
    const stepLen = Math.min(d, speed / 60);
    e.pos.x += (dx / d) * stepLen;
    e.pos.y += (dy / d) * stepLen;
    e.vel.x = (dx / d) * speed;
    e.vel.y = (dy / d) * speed;
  } else {
    e.vel.x = 0;
    e.vel.y = 0;
  }
  e.steering.goal.x = wrapX(tx);
  e.steering.goal.y = wrapY(ty);
  // Face the player while not committed to a pattern.
  e.steering.heading = Math.atan2(wrapDeltaY(py - e.pos.y), wrapDeltaX(px - e.pos.x));
  applyCurrentToBoss(w, st, e);
  w.wrapToArena(e.pos, e.hitbox_radius);
}

function applyCurrentToBoss(w: World, st: BossRuntime, e: Enemy): void {
  const out = st.scratch;
  hazardPush(w, e.pos.x, e.pos.y, out);
  if (out.x === 0 && out.y === 0) return;
  e.pos.x += out.x / 60;
  e.pos.y += out.y / 60;
}

function selectAttack(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy): void {
  const phase = def.phases[st.phase_index];
  const tick = w.tick;
  if (tick < e.stagger_until) return;
  let best = -1;
  let bestReady = 0;
  for (let i = 0; i < phase.attacks.length; i++) {
    const ready = st.next_ready[i];
    if (tick < ready) continue;
    if (best < 0 || ready < bestReady) {
      best = i;
      bestReady = ready;
    }
  }
  if (best < 0) return;
  beginAttack(w, st, def, e, best);
}

function beginAttack(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy, index: number): void {
  const phase = def.phases[st.phase_index];
  const atk = phase.attacks[index];
  const tick = w.tick;
  let tele = atk.telegraphTicks;
  if (st.enraged === 1) tele = Math.round(tele * def.enrageTelegraph);
  if (tele < TELEGRAPH_FLOOR_TICKS) tele = TELEGRAPH_FLOOR_TICKS;
  let cd = atk.cooldownTicks * phase.move.cadence;
  if (st.enraged === 1) cd *= def.enrageCooldown;

  st.attack_index = index;
  st.attack_start = tick;
  st.attack_arm = tick + tele;
  st.stage = 0;
  st.stage_next = st.attack_arm;
  st.hit_flag = 0;
  st.ring_radius = 0;
  st.active_until = st.attack_arm + Math.max(1, atk.activeTicks);
  st.next_ready[index] = tick + Math.max(30, Math.round(cd));

  // Aim locks at telegraph start — that is what makes the preview honest (G1).
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  st.aim_x = px;
  st.aim_y = py;
  st.aim_angle = Math.atan2(wrapDeltaY(py - e.pos.y), wrapDeltaX(px - e.pos.x));
  st.gap_angle = w.rng.spawn.next() * Math.PI * 2;
  // The Stalker re-plants on every slam: "it stations between slams" has to mean it
  // stations next to YOU, or phase 3 is a boss standing somewhere you never go.
  if (def.gait === Gait.Stalker && phase.move.mode === Move.Anchor) plantAnchor(w, st, e, phase.move.far);

  e.attack_state = 1;
  e.telegraph_until = st.attack_arm;
  e.attack_dir.x = Math.cos(st.aim_angle);
  e.attack_dir.y = Math.sin(st.aim_angle);

  // Patterns whose preview IS a hazard zone are placed now, so the zone renders for the
  // full telegraph (mortar discs, last-light safe disc, current lanes).
  if (atk.kind === Kind.Mortar) {
    const n = st.enraged === 1 && def.id === 'boss.abyssal-warden' ? 5 : atk.count;
    const teleS = tele / 60;
    for (let i = 0; i < n; i++) {
      const lead = 0.5 + i * 0.25;
      const jx = (w.rng.spawn.next() * 2 - 1) * 3;
      const jy = (w.rng.spawn.next() * 2 - 1) * 3;
      spawnMortarPool(w, e.id, px + w.player.vel.x * lead + jx, py + w.player.vel.y * lead + jy, teleS);
    }
    st.active_until = st.attack_arm;
  } else if (atk.kind === Kind.LastLight) {
    // Safe disc always within 12 u of the player at telegraph start (bosses.json).
    const a = w.rng.spawn.next() * Math.PI * 2;
    const d = w.rng.spawn.next() * 10;
    spawnLastLight(w, e.id, px + Math.cos(a) * d, py + Math.sin(a) * d, atk.range, tele / 60, atk.damage);
    st.active_until = st.attack_arm;
  } else if (atk.kind === Kind.CurrentTide) {
    spawnBossTide(w, e.id, atk.count, atk.activeTicks / 60, tele / 60);
    st.active_until = st.attack_arm;
  }
}

function fireAttack(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy, atk: CompiledAttack): void {
  switch (atk.kind) {
    case Kind.Volley:
      fireFan(w, st, e, atk, atk.count, atk.spread, atk.speed);
      break;
    case Kind.Barrage:
      fireFan(w, st, e, atk, atk.count, atk.spread, atk.speed);
      st.stage = 1;
      st.stage_next = w.tick + atk.stageGap;
      st.active_until = st.attack_arm + atk.stageGap * atk.stages;
      break;
    case Kind.DartRing:
      fireRadial(w, st, e, atk);
      break;
    case Kind.Ring:
      st.ring_radius = 0;
      st.gap_angle = st.aim_angle + Math.PI; // the gap is always reachable from the aim side
      break;
    case Kind.Lunge:
      st.stage = 1;
      st.stage_next = w.tick + atk.activeTicks + atk.stageGap;
      st.active_until = st.attack_arm + (atk.activeTicks + atk.stageGap) * atk.stages;
      st.recovery_until = st.active_until + 48; // 0.8 s recovery, damage taken ×1.25
      break;
    case Kind.Roll:
      st.active_until = st.attack_arm + atk.activeTicks;
      break;
    case Kind.MeleeArc:
      meleeArc(w, st, e, atk);
      break;
    case Kind.Summon:
      summonAdds(w, st, e, atk);
      break;
    case Kind.ArmSweep:
      break;
    case Kind.TidePull:
      break;
    case Kind.Beam:
      st.active_until = st.attack_arm + Math.round((atk.spread / beamSweepRate(def, st, atk)) * 60);
      break;
    case Kind.Expose:
      st.exposed_until = w.tick + atk.activeTicks;
      break;
    default:
      break;
  }
}

function beamSweepRate(def: CompiledBoss, st: BossRuntime, atk: CompiledAttack): number {
  return st.enraged === 1 && def.id === 'boss.hadal-sovereign' ? 30 : atk.speed;
}

function updateActive(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy, atk: CompiledAttack): void {
  const tick = w.tick;
  switch (atk.kind) {
    case Kind.Barrage:
      if (st.stage < atk.stages && tick >= st.stage_next) {
        st.stage++;
        st.stage_next = tick + atk.stageGap;
        fireFan(w, st, e, atk, atk.count, atk.spread, atk.speed);
      }
      break;
    case Kind.Ring: {
      const prev = st.ring_radius;
      st.ring_radius += atk.speed / 60;
      ringHit(w, st, e, atk, prev, st.ring_radius);
      if (st.ring_radius > 140) st.active_until = tick;
      break;
    }
    case Kind.Lunge:
      lungeStep(w, st, e, atk);
      break;
    case Kind.Roll:
      rollStep(w, st, e, atk);
      break;
    case Kind.ArmSweep:
      armSweepStep(w, st, def, e, atk);
      break;
    case Kind.TidePull:
      pullStep(w, e, atk);
      break;
    case Kind.Beam:
      beamStep(w, st, def, e, atk);
      break;
    default:
      break;
  }
}

// --- pattern implementations ---

function fireFan(w: World, st: BossRuntime, e: Enemy, atk: CompiledAttack, count: number, spreadDeg: number, speed: number): void {
  const full = spreadDeg >= 360;
  const spread = (spreadDeg * Math.PI) / 180;
  const base = full ? st.gap_angle : st.aim_angle - spread * 0.5;
  const stepA = count > 1 ? (full ? (Math.PI * 2) / count : spread / (count - 1)) : 0;
  for (let i = 0; i < count; i++) {
    const a = base + stepA * i;
    spawnBossProjectile(w, st, e, a, speed, atk.damage, 12);
  }
}

function fireRadial(w: World, st: BossRuntime, e: Enemy, atk: CompiledAttack): void {
  const gapHalf = ((atk.gapDeg * Math.PI) / 180) * 0.5;
  const stepA = (Math.PI * 2) / atk.count;
  for (let i = 0; i < atk.count; i++) {
    const a = st.gap_angle + stepA * i;
    if (inAnyGap(a, st.gap_angle, atk.gaps, gapHalf)) continue;
    spawnBossProjectile(w, st, e, a, atk.speed, atk.damage, 14);
  }
}

/** Safe gaps are evenly distributed and marked in the preview (bosses.json). */
function inAnyGap(angle: number, base: number, gaps: number, gapHalf: number): boolean {
  if (gaps <= 0) return false;
  const stepA = (Math.PI * 2) / gaps;
  for (let g = 0; g < gaps; g++) {
    const centre = base + stepA * g + stepA * 0.5;
    if (Math.abs(angleDelta(angle, centre)) <= gapHalf) return true;
  }
  return false;
}

function angleDelta(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

function ringHit(w: World, st: BossRuntime, e: Enemy, atk: CompiledAttack, prev: number, next: number): void {
  if (st.hit_flag !== 0) return;
  const p = w.player;
  const dx = wrapDeltaX(p.pos.x - e.pos.x);
  const dy = wrapDeltaY(p.pos.y - e.pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d + p.hitbox_radius < prev - RING_HALF_THICKNESS || d - p.hitbox_radius > next + RING_HALF_THICKNESS) return;
  if (d < prev - RING_HALF_THICKNESS || d > next + RING_HALF_THICKNESS) return;
  const a = Math.atan2(dy, dx);
  const gapHalf = ((atk.gapDeg * Math.PI) / 180) * 0.5;
  if (inAnyGap(a, st.gap_angle, atk.gaps, gapHalf)) return;
  st.hit_flag = 1;
  damagePlayer(w, atk.damage, e.def_id, e.id);
}

function lungeStep(w: World, st: BossRuntime, e: Enemy, atk: CompiledAttack): void {
  const tick = w.tick;
  const phaseTicks = atk.activeTicks + atk.stageGap;
  const local = (tick - st.attack_arm) % phaseTicks;
  if (local === 0 && tick > st.attack_arm) {
    // Chained lunge re-aims from the end of the previous one.
    st.aim_angle = Math.atan2(wrapDeltaY(w.player.pos.y - e.pos.y), wrapDeltaX(w.player.pos.x - e.pos.x));
    st.hit_flag = 0;
  }
  if (local >= atk.activeTicks) return;
  // Turn clamp 30 deg/s while committed.
  const want = Math.atan2(wrapDeltaY(w.player.pos.y - e.pos.y), wrapDeltaX(w.player.pos.x - e.pos.x));
  const maxTurn = (30 * Math.PI) / 180 / 60;
  const d = angleDelta(want, st.aim_angle);
  st.aim_angle += d > maxTurn ? maxTurn : d < -maxTurn ? -maxTurn : d;
  e.steering.heading = st.aim_angle;
  e.pos.x += Math.cos(st.aim_angle) * (atk.speed / 60);
  e.pos.y += Math.sin(st.aim_angle) * (atk.speed / 60);
  w.wrapToArena(e.pos, e.hitbox_radius);
  contactHit(w, st, e, atk.damage, e.hitbox_radius);
}

function rollStep(w: World, st: BossRuntime, e: Enemy, atk: CompiledAttack): void {
  const cx = Math.cos(st.aim_angle);
  const cy = Math.sin(st.aim_angle);
  e.steering.heading = st.aim_angle;
  e.pos.x += cx * (atk.speed / 60);
  e.pos.y += cy * (atk.speed / 60);
  w.wrapToArena(e.pos, e.hitbox_radius);
  contactHit(w, st, e, atk.damage, e.hitbox_radius);
  // AMD-24, second helping. The old lane length was `activeTicks` × `speed` = 300 × 20/60
  // = 100 u, which on a walled arena was "roll until you hit the far side". The torus has
  // no far side, and 100 u is exactly half the period — so every roll parked the warden at
  // the antipode and it spent the next 25 s walking back. Measured: 44 u mean distance to
  // an idle player, from a boss whose whole read is "it rolls at you".
  //
  // The impact now lands where the roll stops mattering: `ROLL_OVERSHOOT` past the player.
  // The preview lane is unchanged and still a superset of the damaging path (G1).
  const along = wrapDeltaX(w.player.pos.x - e.pos.x) * cx + wrapDeltaY(w.player.pos.y - e.pos.y) * cy;
  if (w.tick + 1 < st.active_until && along > -ROLL_OVERSHOOT) return;
  // Lane-end impact: sheds 4 driftfin, 1.0 s stagger, armour drops (recovery window).
  st.active_until = w.tick;
  e.stagger_until = w.tick + 60;
  st.recovery_until = w.tick + 60;
  for (let i = 0; i < 4; i++) {
    const a = st.aim_angle + Math.PI + (i - 1.5) * 0.4;
    spawnAdd(w, st, e, 'enemy.driftfin', e.pos.x + Math.cos(a) * 4, e.pos.y + Math.sin(a) * 4);
  }
}

function meleeArc(w: World, st: BossRuntime, e: Enemy, atk: CompiledAttack): void {
  const p = w.player;
  const dx = wrapDeltaX(p.pos.x - e.pos.x);
  const dy = wrapDeltaY(p.pos.y - e.pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d > atk.range + e.hitbox_radius + p.hitbox_radius) return;
  const half = ((atk.spread * Math.PI) / 180) * 0.5;
  if (Math.abs(angleDelta(Math.atan2(dy, dx), st.aim_angle)) > half) return;
  damagePlayer(w, atk.damage, e.def_id, e.id);
}

function armSweepStep(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy, atk: CompiledAttack): void {
  const p = w.player;
  const dx = wrapDeltaX(p.pos.x - e.pos.x);
  const dy = wrapDeltaY(p.pos.y - e.pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d > atk.range) return;
  const rate = ((atk.speed * (st.enraged === 1 ? 1.3 : 1)) * Math.PI) / 180;
  const rot = st.aim_angle + rate * ((w.tick - st.attack_arm) / 60);
  const half = ((atk.spread * Math.PI) / 180) * 0.5;
  const stepA = (Math.PI * 2) / atk.count;
  for (let i = 0; i < atk.count; i++) {
    if (Math.abs(angleDelta(Math.atan2(dy, dx), rot + stepA * i)) <= half) {
      damagePlayer(w, atk.damage, e.def_id, e.id);
      return;
    }
  }
}

function pullStep(w: World, e: Enemy, atk: CompiledAttack): void {
  const p = w.player;
  if (p.dash.active) return;
  const dx = wrapDeltaX(e.pos.x - p.pos.x);
  const dy = wrapDeltaY(e.pos.y - p.pos.y);
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d < 1e-3) return;
  p.pos.x += (dx / d) * (atk.speed / 60);
  p.pos.y += (dy / d) * (atk.speed / 60);
  w.wrapToArena(p.pos, p.hitbox_radius);
}

function beamStep(w: World, st: BossRuntime, def: CompiledBoss, e: Enemy, atk: CompiledAttack): void {
  const rate = ((beamSweepRate(def, st, atk) * Math.PI) / 180) / 60;
  const half = ((atk.spread * Math.PI) / 180) * 0.5;
  const angle = st.aim_angle - half + rate * (w.tick - st.attack_arm);
  const p = w.player;
  const dx = wrapDeltaX(p.pos.x - e.pos.x);
  const dy = wrapDeltaY(p.pos.y - e.pos.y);
  const along = dx * Math.cos(angle) + dy * Math.sin(angle);
  if (along < 0) return;
  const across = -dx * Math.sin(angle) + dy * Math.cos(angle);
  if (Math.abs(across) > BEAM_HALF_WIDTH + p.hitbox_radius) return;
  damagePlayer(w, atk.damage, e.def_id, e.id);
  // Sovereign phase 3: the core exposes for 6 s after each beam.
  if (def.id === 'boss.hadal-sovereign' && def.phases[st.phase_index].index >= 3) {
    st.exposed_until = Math.max(st.exposed_until, st.active_until + 360);
  }
}

function contactHit(w: World, st: BossRuntime, e: Enemy, damage: number, radius: number): void {
  const p = w.player;
  const dx = wrapDeltaX(p.pos.x - e.pos.x);
  const dy = wrapDeltaY(p.pos.y - e.pos.y);
  const r = radius + p.hitbox_radius;
  if (dx * dx + dy * dy > r * r) return;
  damagePlayer(w, damage, e.def_id, e.id);
}

// --- summons (§8.11: portals ≥ 1.5 s, never inside 12 u) ---

function summonAdds(w: World, st: BossRuntime, e: Enemy, atk: CompiledAttack): void {
  if (countAdds(w, st) >= atk.maxAlive) return;
  const n = atk.count;
  for (let i = 0; i < n; i++) {
    if (countAdds(w, st) >= atk.maxAlive) break;
    placeRimPoint(w, st);
    spawnAdd(w, st, e, atk.primary, st.scratch.x, st.scratch.y);
  }
  if (atk.secondary !== '' && countAdds(w, st) < atk.maxAlive) {
    placeRimPoint(w, st);
    spawnAdd(w, st, e, atk.secondary, st.scratch.x, st.scratch.y);
  }
}

function placeRimPoint(w: World, st: BossRuntime): void {
  const p = w.player;
  for (let attempt = 0; attempt < 8; attempt++) {
    const a = w.rng.spawn.next() * Math.PI * 2;
    const d = SPAWN_SAFE_R + 4 + w.rng.spawn.next() * 20;
    // Wrapping keeps the portal on the ring the player can read; clamping used to drag it
    // inward (and sometimes inside the 12 u safe radius) whenever the player hugged an edge.
    const cx = wrapX(p.pos.x + Math.cos(a) * d);
    const cy = wrapY(p.pos.y + Math.sin(a) * d);
    const ddx = wrapDeltaX(cx - p.pos.x);
    const ddy = wrapDeltaY(cy - p.pos.y);
    if (ddx * ddx + ddy * ddy >= SPAWN_SAFE_R * SPAWN_SAFE_R) {
      st.scratch.x = cx;
      st.scratch.y = cy;
      return;
    }
  }
  st.scratch.x = wrapX(p.pos.x + SPAWN_SAFE_R + 4);
  st.scratch.y = wrapY(p.pos.y);
}

function spawnAdd(w: World, st: BossRuntime, e: Enemy, defId: string, x: number, y: number): void {
  const p = w.player;
  const cx = wrapX(x);
  const cy = wrapY(y);
  const dx = wrapDeltaX(cx - p.pos.x);
  const dy = wrapDeltaY(cy - p.pos.y);
  if (dx * dx + dy * dy < SPAWN_SAFE_R * SPAWN_SAFE_R) return;
  if (w.enemies.count >= w.enemies.capacity - 4) return;
  const spawned = spawnEnemyExternal(w, defId, cx, cy, false, '', '', SUMMON_PORTAL_TICKS);
  if (spawned !== null) st.adds_spawned++;
}

function countAdds(w: World, st: BossRuntime): number {
  const list = w.enemies.liveIndices();
  let n = 0;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (!e.is_boss && e.spawn_tick >= st.spawn_tick) n++;
  }
  return n;
}

// --- boss projectiles (shared pool, faction 'enemy') ---

function spawnBossProjectile(w: World, st: BossRuntime, e: Enemy, angle: number, speed: number, damage: number, visualClass: number): void {
  const pr = w.projectiles.acquire();
  if (pr === null) {
    w.bus.emit('pool_exhausted', { pool: 'projectiles', denied_kind: e.def_id });
    return;
  }
  const off = e.hitbox_radius + 0.6;
  pr.id = w.allocId();
  pr.weapon_id = e.def_id;
  pr.owner_id = e.id;
  pr.faction = 'enemy';
  pr.targetable = false;
  pr.pos.x = wrapX(e.pos.x + Math.cos(angle) * off);
  pr.pos.y = wrapY(e.pos.y + Math.sin(angle) * off);
  pr.vel.x = Math.cos(angle) * speed;
  pr.vel.y = Math.sin(angle) * speed;
  pr.hp = 1;
  pr.max_hp = 1;
  pr.hitbox_radius = BOSS_PROJECTILE_RADIUS;
  pr.damage_snapshot = damage;
  pr.crit_snapshot = false;
  pr.area_snapshot = 0;
  pr.knockback_snapshot = 0;
  pr.penetration_left = 0;
  pr.ricochet_left = 0;
  pr.hit_count = 0;
  pr.behaviour = 0;
  pr.phase = angle;
  pr.orbit_radius = 0;
  pr.homing_strength = 0;
  pr.target_id = 0;
  pr.status_on_hit = '';
  pr.visual_class = visualClass;
  pr.status_count = 0;
  pr.spawn_tick = w.tick;
  pr.spawn_tick_f = w.tick;
  pr.expire_tick = w.tick + 600;
  st.projOwned[pr.pool_index] = 1;
}

function stepBossProjectiles(w: World, st: BossRuntime): void {
  const list = w.projectiles.liveIndices();
  const p = w.player;
  for (let i = list.length - 1; i >= 0; i--) {
    const idx = list[i];
    if (st.projOwned[idx] === 0) continue;
    const pr: Projectile = w.projectiles.slots[idx];
    pr.pos.x += pr.vel.x / 60;
    pr.pos.y += pr.vel.y / 60;
    wrapPos(pr.pos);
    const dx = wrapDeltaX(p.pos.x - pr.pos.x);
    const dy = wrapDeltaY(p.pos.y - pr.pos.y);
    const r = pr.hitbox_radius + p.hitbox_radius;
    if (dx * dx + dy * dy <= r * r) {
      damagePlayer(w, pr.damage_snapshot, pr.weapon_id, pr.owner_id);
      st.projOwned[idx] = 0;
      w.projectiles.release(pr);
      continue;
    }
    // Nothing leaves a torus, so the 10 s lifetime is the only despawn.
    if (w.tick >= pr.expire_tick) {
      st.projOwned[idx] = 0;
      w.projectiles.release(pr);
    }
  }
}

/** Every boss damage path funnels through the shared R4 sink (`hazards.ts`). */
function damagePlayer(w: World, amount: number, sourceDefId: string, sourceId: number): void {
  applyPlayerDamage(w, amount, sourceDefId, sourceId);
}

// --- defeat ---

function onBossDefeated(w: World, st: BossRuntime): void {
  const bossId = st.boss_id;
  if (bossId === '') return;
  releaseHazardsOwnedBy(w, st.entity_id);
  st.defeated_tick = w.tick;
  st.last_defeated_id = bossId;
  st.boss_id = '';
  st.entity_id = 0;
  st.pool_index = -1;
  st.attack_index = -1;
  st.exposed_until = 0;
  st.recovery_until = 0;
  if (st.contract_start >= 0) {
    st.contract_start = -1;
    w.arenaHalfW = 100;
    w.arenaHalfH = 100;
  }
  w.bus.emit('boss_defeated', { boss_id: bossId, fight_duration_s: (w.tick - st.spawn_tick) / 60 });
  // Spine §4 — victory on the final boss kill ends the run.
  if (bossId === 'boss.hadal-sovereign') setVictory(w);
}

// --- save / resume (A.6) ---

export function bossSnapshot(w: World): BossState {
  const st = runtime(w);
  return {
    boss_id: st.boss_id,
    entity_id: st.entity_id,
    pool_index: st.pool_index,
    spawn_tick: st.spawn_tick,
    phase_index: st.phase_index,
    enraged: st.enraged,
    attack_index: st.attack_index,
    attack_start: st.attack_start,
    attack_arm: st.attack_arm,
    active_until: st.active_until,
    stage: st.stage,
    stage_next: st.stage_next,
    next_ready: st.next_ready.slice(),
    aim_angle: st.aim_angle,
    aim_x: st.aim_x,
    aim_y: st.aim_y,
    anchor_x: st.anchor_x,
    anchor_y: st.anchor_y,
    anchor_set: st.anchor_set,
    orbit_sign: st.orbit_sign,
    prev_dist: st.prev_dist,
    gap_angle: st.gap_angle,
    ring_radius: st.ring_radius,
    hit_flag: st.hit_flag,
    exposed_until: st.exposed_until,
    recovery_until: st.recovery_until,
    contract_start: st.contract_start,
    vents_done: st.vents_done,
    adds_spawned: st.adds_spawned,
    defeated_tick: st.defeated_tick,
    last_defeated_id: st.last_defeated_id,
    victory_pending: st.victory_pending,
  };
}

export function bossRestore(w: World, s: BossState): void {
  const st = runtime(w);
  st.boss_id = s.boss_id;
  st.entity_id = s.entity_id;
  st.pool_index = s.pool_index;
  st.spawn_tick = s.spawn_tick;
  st.phase_index = s.phase_index;
  st.enraged = s.enraged;
  st.attack_index = s.attack_index;
  st.attack_start = s.attack_start;
  st.attack_arm = s.attack_arm;
  st.active_until = s.active_until;
  st.stage = s.stage;
  st.stage_next = s.stage_next;
  for (let i = 0; i < st.next_ready.length; i++) st.next_ready[i] = i < s.next_ready.length ? s.next_ready[i] : 0;
  st.aim_angle = s.aim_angle;
  st.aim_x = s.aim_x;
  st.aim_y = s.aim_y;
  // Saves written before the AMD-24 steering fix carry no post; re-plant on the next tick.
  st.anchor_x = wrapX(s.anchor_x === undefined ? 0 : s.anchor_x);
  st.anchor_y = wrapY(s.anchor_y === undefined ? 0 : s.anchor_y);
  st.anchor_set = s.anchor_set === undefined ? 0 : s.anchor_set;
  st.orbit_sign = s.orbit_sign === undefined || s.orbit_sign === 0 ? 1 : s.orbit_sign;
  st.prev_dist = s.prev_dist === undefined ? -1 : s.prev_dist;
  st.gap_angle = s.gap_angle;
  st.ring_radius = s.ring_radius;
  st.hit_flag = s.hit_flag;
  st.exposed_until = s.exposed_until;
  st.recovery_until = s.recovery_until;
  st.contract_start = s.contract_start;
  st.vents_done = s.vents_done;
  st.adds_spawned = s.adds_spawned;
  st.defeated_tick = s.defeated_tick;
  st.last_defeated_id = s.last_defeated_id;
  st.victory_pending = s.victory_pending;
}

export function bossReset(w: World): void {
  const st = runtime(w);
  st.boss_id = '';
  st.entity_id = 0;
  st.pool_index = -1;
  st.phase_index = 0;
  st.enraged = 0;
  st.attack_index = -1;
  st.active_until = 0;
  st.exposed_until = 0;
  st.recovery_until = 0;
  st.anchor_x = 0;
  st.anchor_y = 0;
  st.anchor_set = 0;
  st.orbit_sign = 1;
  st.prev_dist = -1;
  st.contract_start = -1;
  st.vents_done = 0;
  st.adds_spawned = 0;
  st.defeated_tick = -1;
  st.last_defeated_id = '';
  st.victory_pending = 0;
  st.projOwned.fill(0);
  for (let i = 0; i < st.next_ready.length; i++) st.next_ready[i] = 0;
}
